'use client';

import { ChevronDownIcon, PaperclipIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface QueueMessagePart {
  type: string;
  text?: string;
  url?: string;
  filename?: string;
  mediaType?: string;
}

export interface QueueMessage {
  id: string;
  parts: QueueMessagePart[];
}

export interface QueueTodo {
  id: string;
  title: string;
  description?: string;
  status?: 'pending' | 'completed' | 'in_progress';
}

export type QueueItemProps = ComponentProps<'li'>;

export const QueueItem = ({ className, ...props }: QueueItemProps) => (
  <li
    className={cn(
      'group flex flex-col gap-1 rounded-md px-3 py-1 text-sm transition-colors hover:bg-muted',
      className,
    )}
    {...props}
  />
);

export type QueueItemIndicatorProps = ComponentProps<'span'> & {
  status?: 'pending' | 'completed' | 'in_progress';
};

export const QueueItemIndicator = ({
  status = 'pending',
  className,
  ...props
}: QueueItemIndicatorProps) => (
  <span
    className={cn(
      'relative inline-flex size-6 shrink-0 items-center justify-center rounded-full',
      status === 'completed' && 'bg-[#ECE7FF]',
      status === 'in_progress' && 'bg-[#FFF7ED]',
      status === 'pending' && 'bg-muted/40',
      className,
    )}
    {...props}
  >
    <span
      className={cn(
        'size-3.5 rounded-full',
        status === 'completed' && 'bg-[#6D28D9]',
        status === 'in_progress' && 'animate-pulse bg-[#F97316]',
        status === 'pending' && 'bg-muted-foreground/50',
      )}
    />
  </span>
);

export type QueueItemContentProps = ComponentProps<'span'> & {
  status?: 'pending' | 'completed' | 'in_progress';
};

export const QueueItemContent = ({
  status = 'pending',
  className,
  ...props
}: QueueItemContentProps) => (
  <span
    className={cn(
      'line-clamp-1 grow break-words',
      status === 'completed' && 'text-foreground',
      status === 'in_progress' && 'font-medium text-[#4338CA]',
      status === 'pending' && 'text-muted-foreground',
      className,
    )}
    {...props}
  />
);

export type QueueListProps = ComponentProps<typeof ScrollArea>;

export const QueueList = ({ children, className, ...props }: QueueListProps) => (
  <ScrollArea className={cn('-mb-1 mt-2', className)} {...props}>
    <div className="max-h-40 pr-4">
      <ul>{children}</ul>
    </div>
  </ScrollArea>
);

export type QueueSectionProps = ComponentProps<typeof Collapsible>;

export const QueueSection = ({ className, defaultOpen = true, ...props }: QueueSectionProps) => (
  <Collapsible className={cn(className)} defaultOpen={defaultOpen} {...props} />
);

export type QueueSectionTriggerProps = ComponentProps<'button'>;

export const QueueSectionTrigger = ({
  children,
  className,
  ...props
}: QueueSectionTriggerProps) => (
  <CollapsibleTrigger asChild>
    <button
      className={cn(
        'group flex w-full items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-left font-medium text-muted-foreground text-sm transition-colors hover:bg-muted',
        className,
      )}
      type="button"
      {...props}
    >
      {children}
    </button>
  </CollapsibleTrigger>
);

export type QueueSectionLabelProps = ComponentProps<'span'> & {
  count?: number;
  label: string;
  icon?: React.ReactNode;
};

export const QueueSectionLabel = ({
  count,
  label,
  icon,
  className,
  ...props
}: QueueSectionLabelProps) => (
  <span className={cn('flex items-center gap-2', className)} {...props}>
    <ChevronDownIcon className="group-data-[state=closed]:-rotate-90 size-4 transition-transform" />
    {icon}
    <span>
      {count} {label}
    </span>
  </span>
);

export type QueueSectionContentProps = ComponentProps<typeof CollapsibleContent>;

export const QueueSectionContent = ({ className, ...props }: QueueSectionContentProps) => (
  <CollapsibleContent className={cn(className)} {...props} />
);

export type QueueProps = ComponentProps<'div'>;

export const Queue = ({ className, ...props }: QueueProps) => (
  <div
    className={cn(
      'flex flex-col gap-2 rounded-xl border border-border bg-background px-3 pt-2 pb-2 shadow-xs',
      className,
    )}
    {...props}
  />
);

/** Renders a write_todos tool call as a Queue UI */
export function TodosQueue({
  todos,
  defaultOpen = true,
}: {
  todos: QueueTodo[];
  /** When false, the Tasks section starts collapsed (e.g. older queues when a newer write_todos exists). */
  defaultOpen?: boolean;
}) {
  if (todos.length === 0) return null;

  return (
    <Queue className="px-4 py-3">
      <QueueSection defaultOpen={defaultOpen}>
        <QueueSectionTrigger className="px-0 py-0 bg-transparent hover:bg-transparent">
          <QueueSectionLabel count={todos.length} label="Tasks" />
        </QueueSectionTrigger>
        <QueueSectionContent>
          <QueueList className="mt-3">
            <div className="relative">
              <span
                className="pointer-events-none absolute bottom-3 left-3 top-3 z-0 w-[1.5px] bg-[#E8E3F3]"
                aria-hidden
              />
              {todos.map((todo) => (
                <QueueItem key={todo.id} className="px-0 py-1.5 hover:bg-transparent">
                  <div className="relative z-10 flex items-center gap-3">
                    <QueueItemIndicator status={todo.status ?? 'pending'} />
                    <QueueItemContent status={todo.status ?? 'pending'}>
                      {todo.title || todo.id}
                    </QueueItemContent>
                  </div>
                </QueueItem>
              ))}
            </div>
          </QueueList>
        </QueueSectionContent>
      </QueueSection>
    </Queue>
  );
}
